import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { AddPhotoAlternate as AddPhotoIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getApiUrl, API_BASE_URL, API_ENDPOINTS } from '../../config/api';
import { compressImage } from '../../utils/imageCompression';

interface StorageItem {
  id: string;
  name: string;
  description: string;
  category: 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools' | 'discovery_space' | 'stickers' | 'workshop' | 'kitchen';
  quantity: number;
  storeQuantity?: number;
  takenQuantity?: number;
  minQuantity: number;
  unit: string;
  location: string;
  supplier: string;
  barkodas?: string;
  prekesKodas?: string;
  fabisKodas?: string;
  savikaina?: number;
  pardavimoKaina?: number;
  imageUrl?: string | null;
}

interface EditItemDialogProps {
  open: boolean;
  onClose: () => void;
  onItemUpdated: () => void;
  item: StorageItem | null;
}

const EditItemDialog: React.FC<EditItemDialogProps> = ({
  open,
  onClose,
  onItemUpdated,
  item
}) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantityInput, setQuantityInput] = useState('');
  const [minQuantityInput, setMinQuantityInput] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'business' as 'business' | 'office' | 'exhibition' | 'souvenirs' | 'work_tools' | 'discovery_space' | 'stickers' | 'workshop' | 'kitchen',
    quantity: 0,
    minQuantity: 5,
    unit: 'vnt',
    location: '',
    supplier: '',
    barkodas: '',
    prekesKodas: '',
    fabisKodas: '',
    savikaina: '',
    pardavimoKaina: ''
  });

  // Reset form when item changes
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name,
        description: item.description || '',
        category: item.category,
        quantity: item.quantity,
        minQuantity: item.minQuantity,
        unit: item.unit,
        location: item.location || '',
        supplier: item.supplier || '',
        barkodas: item.barkodas || '',
        prekesKodas: item.prekesKodas || '',
        fabisKodas: item.fabisKodas || '',
        savikaina: item.savikaina !== undefined && item.savikaina !== null ? item.savikaina.toString() : '',
        pardavimoKaina: item.pardavimoKaina !== undefined && item.pardavimoKaina !== null ? item.pardavimoKaina.toString() : ''
      });
      setQuantityInput(String(item.quantity));
      setMinQuantityInput(String(item.minQuantity));
      setImageUrl(item.imageUrl || null);
      setImagePreview(item.imageUrl ? `${API_BASE_URL}${item.imageUrl}` : null);
      setError(null);
    }
  }, [item]);

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Pasirinkite paveikslėlio failą (JPEG, PNG, GIF, WebP)');
      return;
    }
    setError(null);
    setImageUploading(true);
    try {
      const compressed = await compressImage(file);
      const formDataUpload = new FormData();
      formDataUpload.append('image', compressed);
      const res = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_UPLOAD), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formDataUpload
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Nepavyko įkelti nuotraukos');
      }
      const data = await res.json();
      if (data.success && data.imageUrl) {
        setImageUrl(data.imageUrl);
        setImagePreview(`${API_BASE_URL}${data.imageUrl}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Nepavyko įkelti nuotraukos');
    } finally {
      setImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = () => {
    setImageUrl(null);
    setImagePreview(null);
  };

  const handleSubmit = async () => {
    if (!item || !formData.name.trim() || !formData.category) {
      setError('Pavadinimas ir kategorija yra privalomi');
      return;
    }

    const parsedQuantity = quantityInput.trim() === '' ? NaN : parseInt(quantityInput, 10);
    const parsedMinQuantity = minQuantityInput.trim() === '' ? NaN : parseInt(minQuantityInput, 10);

    if (isNaN(parsedQuantity) || parsedQuantity < 0) {
      setError('Įveskite tinkamą kiekį (neneigiamas skaičius)');
      return;
    }

    if (isNaN(parsedMinQuantity) || parsedMinQuantity < 0) {
      setError('Minimalus kiekis turi būti neneigiamas skaičius');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const parseOptionalPrice = (value: string): number | null =>
        value && value.trim() !== '' ? parseFloat(value) : null;

      const pricesEqual = (a: number | null, b: number | null): boolean => {
        if (a === null && b === null) return true;
        if (a === null || b === null || isNaN(a) || isNaN(b)) return false;
        return Math.abs(a - b) < 0.001;
      };

      const itemSavikaina =
        item.savikaina !== undefined && item.savikaina !== null ? Number(item.savikaina) : null;
      const itemPardavimoKaina =
        item.pardavimoKaina !== undefined && item.pardavimoKaina !== null
          ? Number(item.pardavimoKaina)
          : null;
      const formSavikaina = parseOptionalPrice(formData.savikaina);
      const formPardavimoKaina = parseOptionalPrice(formData.pardavimoKaina);
      const currentImageUrl = imageUrl || null;
      const originalImageUrl = item.imageUrl || null;

      const updatePayload: Record<string, unknown> = {};

      if (formData.name !== item.name) updatePayload.name = formData.name;
      if ((formData.description || '') !== (item.description || '')) {
        updatePayload.description = formData.description;
      }
      if (formData.category !== item.category) updatePayload.category = formData.category;
      if (formData.unit !== item.unit) updatePayload.unit = formData.unit;
      if ((formData.location || '') !== (item.location || '')) {
        updatePayload.location = formData.location;
      }
      if ((formData.supplier || '') !== (item.supplier || '')) {
        updatePayload.supplier = formData.supplier;
      }
      if (parsedMinQuantity !== item.minQuantity) updatePayload.minQuantity = parsedMinQuantity;
      if (!pricesEqual(formSavikaina, itemSavikaina)) updatePayload.savikaina = formSavikaina;
      if (!pricesEqual(formPardavimoKaina, itemPardavimoKaina)) {
        updatePayload.pardavimoKaina = formPardavimoKaina;
      }
      if (currentImageUrl !== originalImageUrl) updatePayload.imageUrl = currentImageUrl;

      if (formData.category === 'souvenirs') {
        const newBarkodas = formData.barkodas?.trim() || null;
        const oldBarkodas = item.barkodas?.trim() || null;
        if (newBarkodas !== oldBarkodas) updatePayload.barkodas = newBarkodas;

        const newPrekesKodas = formData.prekesKodas?.trim() || null;
        const oldPrekesKodas = item.prekesKodas?.trim() || null;
        if (newPrekesKodas !== oldPrekesKodas) updatePayload.prekesKodas = newPrekesKodas;

        const newFabisKodas = formData.fabisKodas?.trim() || null;
        const oldFabisKodas = item.fabisKodas?.trim() || null;
        if (newFabisKodas !== oldFabisKodas) updatePayload.fabisKodas = newFabisKodas;
      }

      const metadataChanged = Object.keys(updatePayload).length > 0;
      const quantityChanged = parsedQuantity !== item.quantity;

      if (!metadataChanged && !quantityChanged) {
        onClose();
        return;
      }

      if (metadataChanged) {
        const itemResponse = await fetch(`${getApiUrl(API_ENDPOINTS.STORAGE_ITEMS)}/${item.id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updatePayload)
        });

        if (!itemResponse.ok) {
          const errorData = await itemResponse.json();
          throw new Error(errorData.message || 'Failed to update item');
        }
      }

      if (quantityChanged) {
        const quantityDifference = parsedQuantity - item.quantity;
        const transactionType = quantityDifference > 0 ? 'in' : 'out';
        const absoluteDifference = Math.abs(quantityDifference);

        // Check if we're removing more than available
        if (transactionType === 'out' && absoluteDifference > item.quantity) {
          throw new Error(`Negalima atimti ${absoluteDifference} vienetų. Turimas kiekis: ${item.quantity}`);
        }

        const savikainaForRestock = transactionType === 'in'
          ? (formData.savikaina && formData.savikaina.trim() !== '' ? parseFloat(formData.savikaina as string) : (item.savikaina != null ? Number(item.savikaina) : null))
          : null;
        const transactionResponse = await fetch(getApiUrl(API_ENDPOINTS.STORAGE_RESTOCK), {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            itemId: item.id,
            quantity: absoluteDifference,
            notes: `Kiekio korekcija per redagavimą: ${item.quantity} → ${parsedQuantity}`,
            type: transactionType,
            ...(savikainaForRestock != null && !isNaN(savikainaForRestock) ? { savikaina: savikainaForRestock } : {})
          })
        });

        if (!transactionResponse.ok) {
          const errorData = await transactionResponse.json();
          throw new Error(errorData.message || 'Failed to create transaction record');
        }
      }

      onItemUpdated();
      onClose();
    } catch (err) {
      console.error('Error updating storage item:', err);
      setError(err instanceof Error ? err.message : 'Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ color: '#7b1fa2', fontWeight: 'bold' }}>
        ✏️ Redaguoti prekę ir kiekį: {item.name}
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
          {/* Name */}
          <TextField
            label="Prekės pavadinimas *"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
            required
            sx={{ gridColumn: 'span 2' }}
          />

          {/* Description */}
          <TextField
            label="Aprašymas"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={3}
            sx={{ gridColumn: 'span 2' }}
          />

          {/* Optional image upload */}
          <Box sx={{ gridColumn: 'span 2' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Nuotrauka (neprivaloma)
            </Typography>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleImageChange}
              style={{ display: 'none' }}
              id="edit-item-image-upload"
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                component="label"
                htmlFor="edit-item-image-upload"
                startIcon={<AddPhotoIcon />}
                disabled={imageUploading}
                sx={{ borderColor: '#7b1fa2', color: '#7b1fa2' }}
              >
                {imageUploading ? 'Kraunama...' : (imagePreview ? 'Pakeisti nuotrauką' : 'Įkelti nuotrauką')}
              </Button>
              {imagePreview && (
                <>
                  <Box
                    component="img"
                    src={imagePreview}
                    alt="Preview"
                    sx={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                  />
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleRemoveImage}
                  >
                    Pašalinti
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {/* Category */}
          <FormControl fullWidth required>
            <InputLabel>Kategorija *</InputLabel>
            <Select
              value={formData.category}
              label="Kategorija *"
              onChange={(e) => handleInputChange('category', e.target.value)}
            >
              <MenuItem value="business">Ūkinės prekės</MenuItem>
              <MenuItem value="office">Kanceliarinės prekės</MenuItem>
              <MenuItem value="exhibition">Ekspozicija</MenuItem>
              <MenuItem value="discovery_space">Atradimų erdvė</MenuItem>
              <MenuItem value="souvenirs">Suvenyrai</MenuItem>
              <MenuItem value="stickers">Lipdukai</MenuItem>
              <MenuItem value="work_tools">Darbo reikmenys</MenuItem>
              <MenuItem value="workshop">Asmeniniai</MenuItem>
              <MenuItem value="kitchen">Virtuvėlė</MenuItem>
            </Select>
          </FormControl>

          {/* Unit */}
          <FormControl fullWidth>
            <InputLabel>Matavimo vienetas</InputLabel>
            <Select
              value={formData.unit}
              label="Matavimo vienetas"
              onChange={(e) => handleInputChange('unit', e.target.value)}
            >
              <MenuItem value="vnt">Vienetai (vnt)</MenuItem>
              <MenuItem value="kg">Kilogramai (kg)</MenuItem>
              <MenuItem value="m">Metrai (m)</MenuItem>
              <MenuItem value="l">Litrai (l)</MenuItem>
              <MenuItem value="pak">Pakuotės (pak)</MenuItem>
              <MenuItem value="kom">Komplektai (kom)</MenuItem>
            </Select>
          </FormControl>

          {/* Quantity (warehouse — main stock) */}
          <TextField
            label={
              formData.category === 'souvenirs' || formData.category === 'workshop' || formData.category === 'kitchen'
                ? t(
                    formData.category === 'souvenirs'
                      ? 'storage.souvenirs.warehouseQuantityLabel'
                      : formData.category === 'kitchen'
                        ? 'storage.kitchen.warehouseQuantityLabel'
                        : 'storage.workshop.warehouseQuantityLabel'
                  )
                : 'Naujas kiekis'
            }
            type="number"
            value={quantityInput}
            onChange={(e) => setQuantityInput(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
            }}
            helperText={
              formData.category === 'souvenirs'
                ? t('storage.souvenirs.warehouseQuantityEditHint')
                : formData.category === 'workshop'
                  ? t('storage.workshop.warehouseQuantityEditHint')
                  : formData.category === 'kitchen'
                    ? t('storage.kitchen.warehouseQuantityEditHint')
                    : 'Naujas prekės kiekis (bus sukurtas operacijos įrašas)'
            }
          />

          {formData.category === 'souvenirs' && (
            <TextField
              label={t('storage.souvenirs.storeQuantityLabel')}
              type="number"
              value={item.storeQuantity ?? 0}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
              }}
              helperText={t('storage.souvenirs.returnHintEdit')}
            />
          )}

          {(formData.category === 'workshop' || formData.category === 'kitchen') && (
            <TextField
              label={t(formData.category === 'kitchen' ? 'storage.kitchen.takenQuantityLabel' : 'storage.workshop.takenQuantityLabel')}
              type="number"
              value={item.takenQuantity ?? 0}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
              }}
              helperText={t(formData.category === 'kitchen' ? 'storage.kitchen.returnHintEdit' : 'storage.workshop.returnHintEdit')}
            />
          )}

          {/* Min Quantity */}
          <TextField
            label="Minimalus kiekis"
            type="number"
            value={minQuantityInput}
            onChange={(e) => setMinQuantityInput(e.target.value)}
            fullWidth
            inputProps={{ min: 0 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">{formData.unit}</InputAdornment>
            }}
            helperText="Kiekis, kurį pasiekus bus rodomas įspėjimas"
          />

          {/* Location */}
          <TextField
            label="Sandėlio vieta"
            value={formData.location}
            onChange={(e) => handleInputChange('location', e.target.value)}
            fullWidth
            placeholder="pvz.: A1, B2, C3"
          />

          {/* Supplier */}
          <TextField
            label="Tiekėjas"
            value={formData.supplier}
            onChange={(e) => handleInputChange('supplier', e.target.value)}
            fullWidth
          />

          {/* Savikaina (Cost Price) */}
          <TextField
            label="Savikaina (EUR)"
            type="number"
            value={formData.savikaina}
            onChange={(e) => handleInputChange('savikaina', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">EUR</InputAdornment>
            }}
            placeholder="0.00"
          />

          {/* Pardavimo kaina (Selling Price) */}
          <TextField
            label="Pardavimo kaina (EUR)"
            type="number"
            value={formData.pardavimoKaina}
            onChange={(e) => handleInputChange('pardavimoKaina', e.target.value)}
            fullWidth
            inputProps={{ step: '0.01', min: '0' }}
            InputProps={{
              endAdornment: <InputAdornment position="end">EUR</InputAdornment>
            }}
            placeholder="0.00"
          />

          {/* Barkodas - only show for souvenirs category */}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Barkodas"
              value={formData.barkodas}
              onChange={(e) => handleInputChange('barkodas', e.target.value)}
              fullWidth
              placeholder="pvz.: 123456789012"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
          {/* Prekės kodas - only show for souvenirs category */}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Prekės kodas"
              value={formData.prekesKodas}
              onChange={(e) => handleInputChange('prekesKodas', e.target.value)}
              fullWidth
              placeholder="pvz.: Kojinės 000577"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
          {formData.category === 'souvenirs' && (
            <TextField
              label="Fabis kodas"
              value={formData.fabisKodas}
              onChange={(e) => handleInputChange('fabisKodas', e.target.value)}
              fullWidth
              placeholder="pvz.: FAB-001"
              sx={{ gridColumn: 'span 2' }}
            />
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Pastaba:</strong> Atnaujinus prekę, visi pakeitimai bus išsaugoti. 
            Jei keičiate kiekį, automatiškai bus sukurtas operacijos įrašas sandėlio žurnale.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} variant="outlined" disabled={loading}>
          Atšaukti
        </Button>
        
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{
            backgroundColor: '#7b1fa2',
            '&:hover': { backgroundColor: '#6a1b9a' }
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Atnaujinama...
            </>
          ) : (
            'Atnaujinti prekę ir kiekį'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditItemDialog;
